import { useCallback, useEffect, useState } from 'react'
import { PageDto } from '../types/page.types'

const oneTrustBannerId: Readonly<string> = 'onetrust-banner-sdk'
const oneTrustPolicyContainerId: Readonly<string> = 'onetrust-policy'
const oneTrustTextContainerId: Readonly<string> = 'onetrust-policy-text'
const oneTrustSettingsButtonId: Readonly<string> = 'onetrust-pc-btn-handler'
const oneTrustRejectButtonId: Readonly<string> = 'onetrust-reject-all-handler'
const oneTrustAcceptButtonId: Readonly<string> = 'onetrust-accept-btn-handler'
const oneTrustCloseButtonContainerId: Readonly<string> = 'onetrust-close-btn-container'

const policyClasses = ['!my-8']
const bannerClasses = ['!bg-white', '!text-black', '!outline-0', '!shadow-none', 'lg:!pt-2']

const textClasses = [
  '!text-foreground',
  '!text-base',
  '!text-balance',
  '[&_a[href]]:!p-0',
  '[&_a[href]]:!m-0',
  '[&_a[href]]:!text-current',
  '[&_a[href]]:!text-base',
  '[&_a[href]]:!font-normal',
]

const buttonClassesBase = [
  '!text-base',
  '!font-normal',
  '!no-underline',
  '!uppercase',
  '!px-miista-button-padding-x-default',
  '!py-0',
  '!h-miista-button-height-default',
  '!leading-miista-button-height-default',
  '!rounded-lg',
  '!border',
  '!border-solid',
  '!border-transparent',
  'xl:!mt-[2em]',
]

const buttonClassesPrimary = [
  ...buttonClassesBase,
  '!bg-black',
  'hover:!bg-black',
  '!text-white',
  '!h-miista-button-height-large',
  '!leading-miista-button-height-large',
  '!mb-8',
  'xl:!mb-0',
]

const buttonClassesSecondary = [...buttonClassesBase, '!bg-secondary/20', '!text-secondary-foreground', 'hover:!bg-white/20', 'hover:!backdrop-blur-xs', 'hover:!border-foreground']

const closeIconClasses = ['!top-0', '!right-0']

/**
 * Observer to change presentation of OneTrust/CookiePro banner.
 * Not ideal, ideally we only stick to the styling abilities in
 * the OneTrust admin.
 *
 * @see https://app.cookiepro.com/cookies/templates
 */
const useOneTrustBanner = (pageDto: PageDto) => {
  const [banner, setBanner] = useState<HTMLElement | null>(null)

  const addBannerClasses = useCallback(
    (banner: HTMLElement) => {
      banner.classList.add(...bannerClasses)

      const policyContainer = document.getElementById(oneTrustPolicyContainerId)
      const textContainer = document.getElementById(oneTrustTextContainerId)
      const closeButtonContainer = document.getElementById(oneTrustCloseButtonContainerId)

      if (policyContainer) {
        policyContainer.classList.add(...policyClasses)
      }

      if (textContainer) {
        textContainer.classList.add(...textClasses)

        textContainer.innerHTML = (
          pageDto.dictionary?.cookies?.weusecookiesfor ||
          'We use cookies to provide you a personalized shopping experience. To find out more and learn how to opt out, read our <a href="%1">cookie policy.</a>'
        ).replace('%1', '/legal')
      }

      if (closeButtonContainer) {
        banner.querySelectorAll('button').forEach((button) => {
          button.classList.add(...closeIconClasses)
        })
      }

      banner.querySelectorAll('button').forEach((button) => {
        if (button.parentElement?.id !== oneTrustCloseButtonContainerId) {
          if (button?.id === oneTrustAcceptButtonId) {
            button.classList.add(...buttonClassesPrimary)
            button.textContent = pageDto.dictionary?.cookies?.acceptall || 'Accept all'
          } else {
            button.classList.add(...buttonClassesSecondary)

            if (button?.id === oneTrustSettingsButtonId) {
              button.textContent = pageDto.dictionary?.cookies?.cookiesettings || 'Cookie settings'
            } else if (button?.id === oneTrustRejectButtonId) {
              // One can reject cookies via settings modal.
              button.remove()
            }
          }
        }
      })
    },
    [pageDto.dictionary?.cookies?.acceptall, pageDto.dictionary?.cookies?.cookiesettings, pageDto.dictionary?.cookies?.weusecookiesfor],
  )

  useEffect(() => {
    if (banner) {
      addBannerClasses(banner)
    }

    document.documentElement.style.setProperty(
      '--spacing-miista-bottom-offset',
      banner ? `calc(${banner.offsetHeight}px + var(--spacing-miista-page-padding))` : 'var(--spacing-miista-page-padding)',
    )
  }, [addBannerClasses, banner])

  // Upon banner close, set state.
  useEffect(() => {
    const checkIfClosed = () => {
      if (window?.Optanon?.IsAlertBoxClosed()) {
        setBanner(null)
      }
    }

    window.addEventListener('OneTrustGroupsUpdated', checkIfClosed)

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', checkIfClosed)
    }
  }, [])

  useEffect(() => {
    // Page load.
    const existingBanner = document.getElementById(oneTrustBannerId)
    setBanner(existingBanner)

    // On document mutations.
    const handleMutations = (mutations: MutationRecord[]) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement && node.id === oneTrustBannerId) {
            setBanner(node)

            return
          }
        }
      }
    }

    const observer = new MutationObserver(handleMutations)

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [])
}

export default useOneTrustBanner
